/**
 * What the copilot is allowed to do to the screen.
 *
 * A closed set of six intents, each one mapping onto a state value the curate
 * screen already had. The copilot does not render anything and does not reach
 * into the screen: it returns intents, and this reducer applies them. That is
 * the whole of its authority over the UI.
 *
 * Pure on purpose. Every intent has already been validated against the run on
 * the server -- a view that does not exist or a condition this run does not
 * have never gets here -- so what is left is a fold over plain objects, which
 * is also the part worth having tests for.
 */

export const INTENT_TYPES = [
  "select_view",
  "select_dimension",
  "select_points",
  "toggle_series",
  "show_outliers",
  "navigate",
];

export const INITIAL_STATE = {
  selectedView: null,
  activeCondition: null,
  selected: [],
  highlighted: null,
  showOutliers: false,
  hiddenConditions: [],
  /** A screen to move to, read and cleared by whoever can navigate. */
  pendingScreen: null,
};

function withoutDuplicates(values) {
  return [...new Set(values)];
}

/**
 * One intent applied to the curation state. Anything unrecognised is ignored
 * rather than guessed at -- an intent this build does not know is a build
 * mismatch, not an instruction.
 */
export function applyIntent(state, intent) {
  if (!intent || typeof intent.type !== "string") return state;

  switch (intent.type) {
    case "select_view":
      if (!intent.view || intent.view === state.selectedView) {
        return { ...state, selectedView: intent.view || state.selectedView };
      }
      // Opening a different curve clears what belonged to the last one, exactly
      // as clicking it in the list does.
      return {
        ...state,
        selectedView: intent.view,
        activeCondition: null,
        selected: [],
        highlighted: null,
      };

    case "select_dimension":
      return intent.dimension ? { ...state, activeCondition: intent.dimension } : state;

    case "select_points":
      return { ...state, selected: withoutDuplicates(intent.point_ids || []) };

    case "toggle_series": {
      if (!intent.dimension) return state;
      const hidden = state.hiddenConditions.filter((c) => c !== intent.dimension);
      return {
        ...state,
        hiddenConditions:
          intent.visible === false ? [...hidden, intent.dimension] : hidden,
      };
    }

    case "show_outliers":
      return { ...state, showOutliers: intent.show !== false };

    case "navigate":
      return intent.screen ? { ...state, pendingScreen: intent.screen } : state;

    default:
      return state;
  }
}

export function applyIntents(state, intents) {
  return (intents || []).reduce(applyIntent, state);
}
